/**
 * ======================================================================
 * ENHANCED VARIANT-AWARE CARD UPDATER (Related products + Bridal set)
 * ======================================================================
 * Purpose
 *  - Watches the current product’s variant selection.
 *  - Extracts the selected options by OPTION NAME:
 *      "Metal Type", "Band Shape", "Prongs"
 *  - Updates ALL visible product cards across:
 *      • Default Shopify recommendations: <product-recommendations>, .related-products, .complementary-products
 *      • Bridal set carousel: .prodgroup-slide
 *    so links, images, titles, quick-add and prices reflect the variant that best
 *    matches the same tracked option values on each target product.
 *
 * Matching strategy (per target product card):
 *  1) Try to match ALL tracked options that the target product actually has.
 *  2) Fall back to "Metal Type" only if present.
 *  3) Fall back to any available variant.
 *
 * Smooth image swapping
 *  - Preloads the new image, then crossfades (opacity) to avoid flicker/layout jump.
 *  - Clears srcset to prevent the theme from re-picking sizes after we switch.
 *
 * Performance
 *  - Product JSON responses are cached by handle (single fetch per handle).
 *  - DOM writes are batched via requestAnimationFrame.
 *  - Variant change handling is debounced to reduce churn.
 *
 * URL params
 *  - Adds ?variant plus compact params for tracked options so PDPs can restore:
 *      ?variant-metal=...&variant-band-shape=...&variant-prongs=...
 *
 * ======================================================================
 */

(function () {
  // -----------------------------
  // CONFIG
  // -----------------------------
  const CARD_CONTAINER_SELECTORS = [
    ".complementary-products",
    ".related-products",
    "product-recommendations",
    ".prodgroup-slide", // bridal set carousel slides
  ];

  const CARD_SELECTOR = ".card-wrapper";

  const CARD_IMAGE_SELECTORS = ["img.motion-reduce", "img"];

  const CARD_LINK_SELECTOR = 'a[href*="/products/"]';

  const CARD_PRICE_SELECTORS = [
    "span.price-item.price-item--regular.card-product__price",
    ".price-item--regular",
    ".price",
  ];

  const CARD_VARIANT_TITLE_SELECTORS = [
    "h4.card-product__variant-title",
    ".variant-title",
  ];

  const CARD_MAIN_TITLE_SELECTOR = "h3.card__heading";

  const QUICK_ADD_BUTTON_SELECTORS = [
    "button[data-product-id]",
    "quick-add button",
    'button[name="add"]',
  ];

  const VARIANT_SELECTORS = [
    'input[name="id"]',
    'input[name*="option"]',
    'select[name*="option"]',
    "[data-variant-id]",
    '.product-form__input input[type="radio"]',
  ];

  // Track these option labels EXACTLY as shown in Shopify admin
  const TRACKED_OPTION_LABELS = ["Metal Type", "Band Shape", "Prongs"];

  // Tidy URL param keys for tracked options
  const OPTION_PARAM_KEYS = {
    "Metal Type": "variant-metal",
    "Band Shape": "variant-band-shape",
    "Prongs": "variant-prongs",
  };

  // -----------------------------
  // STATE
  // -----------------------------
  const productCache = {}; // handle -> product JSON
  let debouncedTimer = null;

  // -----------------------------
  // UTILITIES
  // -----------------------------
  const $all = (root, sel) => Array.from(root.querySelectorAll(sel));
  const $ = (root, sel) => root.querySelector(sel);

  function debounce(fn, delay = 120) {
    return function (...args) {
      clearTimeout(debouncedTimer);
      debouncedTimer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function getSelectedVariantId() {
    // Theme variants can be radio, select, hidden 'id', etc.
    const picked =
      document.querySelector('input[name="id"]:checked') ||
      document.querySelector('select[name="id"]') ||
      document.querySelector('input[name="id"][type="hidden"]');
    return picked ? picked.value : null;
  }

  async function fetchProductJsonByHandle(handle) {
    if (productCache[handle]) return productCache[handle];
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(`Fetch product failed: ${handle}`);
    const json = await res.json();
    productCache[handle] = json;
    return json;
  }

  // Shopify AJAX product JSON "options" is usually an array of strings (option names).
  // Be resilient if objects are provided (GraphQL style) by reading .name when present.
  function getOptionNames(productJson) {
    const raw = productJson && productJson.options ? productJson.options : [];
    return raw.map((o) =>
      typeof o === "string" ? o : o && o.name ? String(o.name) : ""
    );
  }

  // Build {"Option Name": "Value"} for a given variant based on product option order.
  function mapOptionNameToValue(productJson, variant) {
    const names = getOptionNames(productJson);
    const vals = [variant.option1, variant.option2, variant.option3];
    const map = {};
    names.forEach((name, i) => {
      if (name) map[String(name).trim()] = vals[i] || null;
    });
    return map;
  }

  // Return only tracked options present on the variant map
  function pickTrackedOptions(optionMap) {
    const out = {};
    TRACKED_OPTION_LABELS.forEach((label) => {
      if (optionMap[label]) out[label] = optionMap[label];
    });
    return out;
  }

  // For a target product, find the "best" matching variant using all tracked options the target actually has.
  function findMatchingVariantByTracked(productJson, tracked) {
    if (!productJson || !Array.isArray(productJson.variants)) return null;

    const targetNames = getOptionNames(productJson).map((n) => String(n).trim());
    const wanted = {};
    for (const [label, value] of Object.entries(tracked || {})) {
      if (targetNames.includes(label)) wanted[label] = value;
    }

    const idxOf = (name) => targetNames.indexOf(name);
    const matchesAll = (v) => {
      const vvals = [v.option1, v.option2, v.option3];
      return Object.entries(wanted).every(([name, val]) => {
        const i = idxOf(name);
        return i >= 0 && vvals[i] === val;
      });
    };

    // 1) Full match
    let found = productJson.variants.find((v) => v.available && matchesAll(v));
    if (found) return found;

    // 2) Fallback: metal-only if applicable
    const metalLabel = "Metal Type";
    if (wanted[metalLabel] && targetNames.includes(metalLabel)) {
      const mi = idxOf(metalLabel);
      found = productJson.variants.find(
        (v) => v.available && [v.option1, v.option2, v.option3][mi] === wanted[metalLabel]
      );
      if (found) return found;
    }

    // 3) Any available
    return productJson.variants.find((v) => v.available) || null;
  }

  function formatPriceLikeExisting(existingText, cents) {
    let currencySymbol = "$";
    const match = existingText ? existingText.match(/[^\d\s,.-]/) : null;
    if (match) currencySymbol = match[0];
    // crude AUD hint if existing starts with 'A'
    currencySymbol = currencySymbol === "A" ? "AUD " : "$";

    const amount = (cents || 0) / 100;
    const formatted = amount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `${currencySymbol}${formatted}`;
  }

  // Build a 500px wide variant image URL (Shopify CDN pattern)
  function to500w(url) {
    if (!url) return url;
    return url.replace(/\.(jpg|jpeg|png|gif)(\?.*)?$/i, "_500x.$1");
  }

  // Preload + crossfade to avoid flicker
  function swapImageSmoothly(imgEl, nextUrl) {
    if (!imgEl || !nextUrl) return;

    const currentUrl = imgEl.currentSrc || imgEl.src || "";
    if (currentUrl === nextUrl) return;

    const pre = new Image();
    pre.decoding = "async";
    pre.loading = "eager";
    pre.onload = () => {
      if (!imgEl.style.transition) imgEl.style.transition = "opacity 200ms ease";
      imgEl.style.opacity = "0.1";

      // Prevent srcset from fighting our chosen URL
      imgEl.srcset = "";
      imgEl.src = nextUrl;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          imgEl.style.opacity = "1";
        });
      });
    };
    pre.src = nextUrl;
  }

  function getBestCardImageEl(card) {
    for (const sel of CARD_IMAGE_SELECTORS) {
      const el = card.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function getFirstExisting(root, selectors) {
    for (const sel of selectors) {
      const el = root.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function getProductHandleFromCard(card) {
    const link = card.querySelector(CARD_LINK_SELECTOR);
    if (!link) return null;
    const parts = link.href.split("/products/");
    if (parts.length < 2) return null;
    return parts[1].split("?")[0].split("#")[0];
  }

  // -----------------------------
  // CORE: APPLY UPDATES TO A SINGLE CARD
  // -----------------------------
  function applyCardUpdate(card, productJson, matchingVariant, trackedOptions) {
    // 1) Update links with ?variant and tracked ?variant-*
    const links = card.querySelectorAll(CARD_LINK_SELECTOR);
    links.forEach((link) => {
      const u = new URL(link.href);
      if (matchingVariant) u.searchParams.set("variant", matchingVariant.id);

      if (trackedOptions && Object.keys(trackedOptions).length) {
        for (const [label, value] of Object.entries(trackedOptions)) {
          const key =
            OPTION_PARAM_KEYS[label] ||
            `variant-${label.toLowerCase().replace(/\s+/g, "-")}`;
          if (value) u.searchParams.set(key, value);
        }
      }
      link.href = u.toString();
    });

    // 2) Image swap
    const hasVariantImage = matchingVariant && matchingVariant.featured_image;
    if (hasVariantImage) {
      const img = getBestCardImageEl(card);
      if (img) {
        const raw =
          matchingVariant.featured_image.src || matchingVariant.featured_image;
        const sized = to500w(raw);

        const current = img.currentSrc || img.src || "";
        if (current !== sized) {
          const metalValue = trackedOptions && trackedOptions["Metal Type"];
          swapImageSmoothly(img, sized);
          if (img.alt && metalValue) {
            img.alt = img.alt.replace(
              /Platinum|18K[^,]*Gold|14K[^,]*Gold|Yellow Gold|White Gold|Rose Gold/gi,
              metalValue
            );
          }
        }
      }
    }

    // 3) Main title
    const mainH3 = card.querySelector(CARD_MAIN_TITLE_SELECTOR);
    if (mainH3) {
      const a = mainH3.querySelector("a");
      if (a) a.textContent = productJson.title || a.textContent;
    }

    // 4) Variant label line
    const variantTitleEl = getFirstExisting(card, CARD_VARIANT_TITLE_SELECTORS);
    if (variantTitleEl && trackedOptions) {
      const metal = trackedOptions["Metal Type"];
      if (metal) {
        variantTitleEl.textContent = metal;
      } else {
        const bits = [];
        if (trackedOptions["Band Shape"]) bits.push(trackedOptions["Band Shape"]);
        if (trackedOptions["Prongs"]) bits.push(trackedOptions["Prongs"]);
        if (bits.length) variantTitleEl.textContent = bits.join(" • ");
      }
    }

    // 5) Price
    const priceEl = getFirstExisting(card, CARD_PRICE_SELECTORS);
    if (priceEl && matchingVariant && typeof matchingVariant.price === "number") {
      priceEl.textContent = formatPriceLikeExisting(
        priceEl.textContent,
        matchingVariant.price
      );
    }

    // 6) Quick add + form id
    const quickBtn = getFirstExisting(card, QUICK_ADD_BUTTON_SELECTORS);
    if (quickBtn && matchingVariant) {
      quickBtn.dataset.variantId = matchingVariant.id;
      const form = card.querySelector('form[action*="/cart/add"]');
      if (form) {
        let idInput = form.querySelector('input[name="id"]');
        if (!idInput) {
          idInput = document.createElement("input");
          idInput.type = "hidden";
          idInput.name = "id";
          form.appendChild(idInput);
        }
        idInput.value = matchingVariant.id;
      }
    }
  }

  // -----------------------------
  // PREP A CARD (FETCH + FIND MATCH), RETURN A CLOSURE TO APPLY
  // -----------------------------
  async function prepareCardUpdate(card, trackedOptions) {
    const handle = getProductHandleFromCard(card);
    if (!handle) return null;

    let productJson;
    try {
      productJson = await fetchProductJsonByHandle(handle);
    } catch (e) {
      console.error("Failed product fetch:", handle, e);
      return null;
    }

    const matching = findMatchingVariantByTracked(productJson, trackedOptions);
    return () => applyCardUpdate(card, productJson, matching, trackedOptions);
  }

  // -----------------------------
  // UPDATE ALL CARDS IN ALL KNOWN CONTAINERS
  // -----------------------------
  async function updateAllCardsForTrackedOptions(trackedOptions) {
    const containers = CARD_CONTAINER_SELECTORS.flatMap((sel) =>
      Array.from(document.querySelectorAll(sel))
    );
    if (containers.length === 0) return;

    const cards = containers.flatMap((c) => $all(c, CARD_SELECTOR));
    if (cards.length === 0) return;

    const updates = await Promise.all(
      cards.map((card) => prepareCardUpdate(card, trackedOptions))
    );

    requestAnimationFrame(() => {
      updates.forEach((apply) => {
        if (apply) apply();
      });
    });
  }

  // -----------------------------
  // READ CURRENT PRODUCT VARIANT → TRACKED OPTIONS
  // -----------------------------
  async function handleVariantChange() {
    const variantId = getSelectedVariantId();
    if (!variantId) return;

    try {
      const res = await fetch(`${window.location.pathname}.js`);
      if (!res.ok) return;
      const product = await res.json();

      const v = product.variants.find((x) => String(x.id) === String(variantId));
      if (!v) return;

      // Build {"Metal Type": "...", "Band Shape": "...", "Prongs": "..."}
      const optionMap = mapOptionNameToValue(product, v);
      const tracked = pickTrackedOptions(optionMap);

      if (!Object.keys(tracked).length) return;

      await updateAllCardsForTrackedOptions(tracked);
    } catch (e) {
      console.error("Failed to resolve current product or variant:", e);
    }
  }

  const debouncedHandleVariantChange = debounce(handleVariantChange, 120);

  // -----------------------------
  // WIRE UP LISTENERS
  // -----------------------------
  function bindVariantListeners() {
    VARIANT_SELECTORS.forEach((sel) => {
      $all(document, sel).forEach((el) => {
        el.addEventListener("change", debouncedHandleVariantChange, {
          passive: true,
        });
        el.addEventListener("input", debouncedHandleVariantChange, {
          passive: true,
        });
      });
    });
  }

  // 1) On initial load
  document.addEventListener("DOMContentLoaded", () => {
    bindVariantListeners();

    // 2) Watch for late-rendered recommendation/bridal blocks
    const observer = new MutationObserver((muts) => {
      let shouldUpdate = false;
      for (const m of muts) {
        if (m.addedNodes) {
          for (const node of m.addedNodes) {
            if (
              node.nodeType === 1 &&
              node.querySelector &&
              node.querySelector(CARD_SELECTOR)
            ) {
              shouldUpdate = true;
              break;
            }
          }
        }
        if (shouldUpdate) break;
      }
      if (shouldUpdate) debouncedHandleVariantChange();
    });

    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
    });

    // 3) Initial pass (allow sections time to mount)
    setTimeout(debouncedHandleVariantChange, 600);
  });
})();
